import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CMSNav from "./nav"; 
import "./CategoryPage.css";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


function SortableCategory({
  node,
  editingId,
  editName,
  editSlug,
  setEditingId,
  setEditSlug,
  setEditName,
  onDelete,
  onUpdate,
  setParentForAdd,
  childrenRenderer, // function to render children (SortableContext per-level)
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <li ref={setNodeRef} style={style} className="category-item">
      <div className="category-row">
        {editingId === node.id ? (
          <div className="category-edit">
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="ویرایش نام دسته"
              className="category-input"
            />
            <input
              value={editSlug}
              onChange={(e) => setEditSlug(e.target.value)}
              placeholder="ویرایش اسلاگ (اختیاری)"
              className="category-input"
            />
            <button onClick={() => onUpdate(node.id, node.parent_id)} className="btn save">
              ذخیره
            </button>
            <button onClick={() => setEditingId(null)} className="btn cancel">
              انصراف
            </button>
          </div>
        ) : (
          <>
            <div className="category-title">{node.name}</div>
            <div className="category-actions">
              <button
                onClick={() => {
                  setParentForAdd(node.id, node.name);
                }}
                className="btn small"
                title="افزودن زیردسته"
              >
                ➕
              </button>

              <button
                onClick={() => {
                  setEditingId(node.id);
                  setEditName(node.name);
                  setEditSlug(node.slug || "");
                }}
                className="btn small"
                title="ویرایش"
              >
                ✏️
              </button>

              <button onClick={() => onDelete(node.id)} className="btn small danger" title="حذف">
                🗑
              </button>

              <button {...listeners} {...attributes} className="btn small drag" title="جابجایی">
                ⠿
              </button>
            </div>
          </>
        )}
      </div>

      {node.children && node.children.length > 0 && (
        <div className="children-wrap">
          {childrenRenderer(node.children)}
        </div>
      )}
    </li>
  );
}

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]); // nested tree
  const [newCategory, setNewCategory] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [parentIdForAdd, setParentIdForAdd] = useState(null);
  const [parentNameForAdd, setParentNameForAdd] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");   
  const [activeId, setActiveId] = useState(null);

  const token = localStorage.getItem("token");

  // Helpers to work with nested tree immutably

  // find node by id (returns node or null)
  const findNode = (tree, id) => {
    for (const n of tree) {
      if (String(n.id) === String(id)) return n;
      if (n.children) {
        const found = findNode(n.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // remove node and return { node, newTree }
  const removeNodeById = (arr, id) => {
    let removed = null;
    const newArr = arr
      .map((item) => {
        if (String(item.id) === String(id)) {
          removed = { ...item }; // include its children
          return null;
        }

        if (item.children && item.children.length) {
          const res = removeNodeById(item.children, id);
          if (res.node) {
            removed = res.node;
            return { ...item, children: res.newTree };
          }
        }

        return item;
      })
      .filter(Boolean);
    return { node: removed, newTree: newArr };
  };

  // insert node under parentId at index (if parentId === null => insert at root)
  const insertNodeAt = (arr, parentId, node, index) => {
    if (parentId === null) {
      const copy = [...arr];
      copy.splice(index, 0, node);
      return copy;
    }

    // recursive search to insert under the correct parent
    return arr.map((item) => {
      if (String(item.id) === String(parentId)) {
        const newChildren = item.children ? [...item.children] : [];
        newChildren.splice(index, 0, node);
        return { ...item, children: newChildren };
      }

      if (item.children && item.children.length) {
        return { ...item, children: insertNodeAt(item.children, parentId, node, index) };
      }

      return item;
    });
  };

  // given a tree and item id, return its parent id (null if root) and index within siblings
  const findParentAndIndex = (tree, id, parentId = null) => {
    for (let i = 0; i < tree.length; i++) {
      const item = tree[i];
      if (String(item.id) === String(id)) return { parentId, index: i };
      if (item.children && item.children.length) {
        const res = findParentAndIndex(item.children, id, item.id);
        if (res) return res;
      }
    }
    return null;
  };

  // Serialize ordering for backend: list of { id, parent_id, sort_order }
  const serializeOrders = (tree, parent = null, out = []) => {
    tree.forEach((node, idx) => {
      out.push({ id: node.id, parent_id: parent, sort_order: idx });
      if (node.children && node.children.length) {
        serializeOrders(node.children, node.id, out);
      }
    });
    return out;
  };

  // Fetch categories (assume backend returns nested tree)
  const fetchCategories = async () => {
    try {
      const res = await fetch("https://api.goharesadaf.ir/api/admin/categories", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to fetch categories");
      const data = await res.json();
      // If backend returns flat list you can convert to tree here
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add
  const handleAdd = async () => {
    if (!newCategory.trim()) {
      alert("Category name cannot be empty.");
      return;
    }
    try {
        const payload = {
        name: newCategory,
        parent_id: parentIdForAdd,
        };
      if (newSlug.trim()) payload.slug = newSlug;
      await fetch("https://api.goharesadaf.ir/api/admin/categories", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      setNewCategory("");
      setParentIdForAdd(null);
      setParentNameForAdd("");
      fetchCategories();
    } catch (err) {
      console.error("Error adding category:", err);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await fetch(`https://api.goharesadaf.ir/api/admin/categories/${id}`, {
        method: "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      fetchCategories();
    } catch (err) {
      console.error("Error deleting category:", err);
    }
  };

  // Update
  const handleUpdate = async (id, currentParentId) => {
    if (!editName.trim()) {
      alert("Category name cannot be empty.");
      return;
    }
    try {
      const payload = {
        name: editName,
        parent_id: currentParentId,
      };
      if (editSlug.trim()) payload.slug = editSlug;
      await fetch(`https://api.goharesadaf.ir/api/admin/categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      setEditingId(null);
      setEditName("");
      setEditSlug("");
      fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Helper to find node in current state tree (ref)
  const findNodeInState = (id) => findNode(categories, id);

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Find source parent & index and destination parent & index in current state
    const src = findParentAndIndex(categories, activeId);
    const dst = findParentAndIndex(categories, overId);
    if (!src || !dst) return;

    const srcParentId = src.parentId;
    const dstParentId = dst.parentId;

    // Remove active node
    const removedRes = removeNodeById(categories, activeId);
    const removedNode = removedRes.node;
    let newTree = removedRes.newTree;

    if (!removedNode) return;

    // If moving into the same parent list (reorder)
    if (String(srcParentId) === String(dstParentId)) {
      // siblings come from parent array in newTree; we need to compute correct insertion index
      // find insertion index relative to newTree (sibling list)
      // If parent is root (null)
      const siblings = srcParentId === null ? newTree : (function findParentChildren(arr, pid) {
        for (const it of arr) {
          if (String(it.id) === String(pid)) return it.children || [];
          if (it.children && it.children.length) {
            const res = findParentChildren(it.children, pid);
            if (res) return res;
          }
        }
        return [];
      })(newTree, srcParentId);

      // compute new index (if dragging down the list the index should adjust)
      let newIndex = siblings.findIndex((s) => String(s.id) === overId);
      if (newIndex === -1) {
        // fallback: append
        newIndex = siblings.length;
      }

      // insert
      newTree = insertNodeAt(newTree, srcParentId, removedNode, newIndex);
    } else {
      // Moving to a different parent: insert under dstParentId at dst.index
      const dstIndex = dst.index;
      // update removedNode parent id
      const nodeToInsert = { ...removedNode, parent_id: dstParentId };
      newTree = insertNodeAt(newTree, dstParentId, nodeToInsert, dstIndex);
    }

    // Update local state first for snappy UI
    setCategories(newTree);

    // Send ordering (and parent changes) to backend
    try {
      const orders = serializeOrders(newTree);
      await fetch("https://api.goharesadaf.ir/api/admin/categories/order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ categories: orders }),
      });
      // optionally fetch fresh tree back from server to ensure consistency
      fetchCategories();
    } catch (err) {
      console.error("Error saving order to backend:", err);
    }
  };

  // children renderer returns its own SortableContext so each level is independently sortable
  const childrenRenderer = (childrenArray) => {
    const items = childrenArray.map((c) => String(c.id));
    return (
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <ul className="category-tree">
          {childrenArray.map((child) => (
            <SortableCategory
              key={child.id}
              node={child}
              editingId={editingId}
              editName={editName}
              editSlug={editSlug}
              setEditingId={setEditingId}
              setEditName={setEditName}
              setEditSlug={setEditSlug}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
              setParentForAdd={(id, name) => {
                setParentIdForAdd(id);
                setParentNameForAdd(name);
              }}
              childrenRenderer={childrenRenderer}
            />
          ))}
        </ul>
      </SortableContext>
    );
  };

  // top-level list items
  const topLevelItems = useMemo(() => categories.map((c) => String(c.id)), [categories]);

  // helper to find node name for DragOverlay
  const activeNode = activeId ? findNodeInState(activeId) : null;

  return (
    <div className="categories-container">

      <CMSNav />

      <h1 className="page-title">دسته‌بندی‌ها</h1>
      <div className="add-category-form">
        <input
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder={`دستۀ جدید ${parentNameForAdd ? `(زیردستۀ ${parentNameForAdd})` : ""}`}
        />
        <input
          value={newSlug}
          onChange={(e) => setNewSlug(e.target.value)}
          placeholder="اسلاگ (اختیاری)"
        />
        <button onClick={handleAdd}>افزودن</button>
        {parentIdForAdd && (
          <button
            onClick={() => {
              setParentIdForAdd(null);
              setParentNameForAdd("");
            }}
          >
            انصراف از افزودن زیردسته
          </button>
        )}
      </div>

      {categories.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={topLevelItems} strategy={verticalListSortingStrategy}>
            <ul className="category-tree root-tree">
              {categories.map((cat) => (
                <SortableCategory
                  key={cat.id}
                  node={cat}
                  editingId={editingId}
                  editName={editName}
                  editSlug={editSlug}
                  setEditingId={setEditingId}
                  setEditName={setEditName}
                  setEditSlug={setEditSlug}
                  onDelete={handleDelete}
                  onUpdate={handleUpdate}
                  setParentForAdd={(id, name) => {
                    setParentIdForAdd(id);
                    setParentNameForAdd(name);
                  }}
                  childrenRenderer={childrenRenderer}
                />
              ))}
            </ul>
          </SortableContext>

          <DragOverlay>
            {activeNode ? (
              <div className="drag-overlay">
                <div className="category-row">
                  <div className="category-title">{activeNode.name}</div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <p>هیچ دسته‌بندی وجود ندارد.</p>
      )}
    </div>
  );
}
